import type {
  OTCFieldsData,
  OTCFixedRateLoanFieldsData,
  OTCFloatingRateLoanFieldsData,
} from '@src/types/OTC/otcProductBuilderTypes';
import { EMPTY_OBJECT, type MapTo } from '@talos/kyoko';
import { useCallback, useMemo, useRef, useState } from 'react';

import { getSliceStrategyForStateWithOTCFields } from '../productDefinitions/getSliceStrategy';

/** Top-level OTC fields Yup validates; optional unset fields may be absent from Object.keys(formData). */
const FIXED_RATE_LOAN_OTC_TOUCH_KEYS = [
  'productType',
  'unitLoanAmount',
  'loanPaymentCcy',
  'termType',
  'term',
  'initialTerm',
  'paymentPeriod',
  'accrualPeriod',
  'dayCountConvention',
] as const satisfies readonly (keyof OTCFixedRateLoanFieldsData)[];

const FLOATING_RATE_LOAN_OTC_TOUCH_KEYS = [
  'productType',
  'unitLoanAmount',
  'termType',
  'term',
  'initialTerm',
  'loanPaymentCcy',
  'paymentPeriod',
  'accrualPeriod',
  'dayCountConvention',
  'rateSource',
  'resetPeriod',
  'prepaymentOption',
  'callOption',
] as const satisfies readonly (keyof OTCFloatingRateLoanFieldsData)[];

interface UseOTCFieldsValidationParams {
  formData: OTCFieldsData;
}

const NO_ERRORS: Partial<MapTo<OTCFieldsData, string>> = EMPTY_OBJECT;
export const useOTCFieldsValidation = ({ formData }: UseOTCFieldsValidationParams) => {
  const [touched, setTouched] = useState<Partial<MapTo<OTCFieldsData, boolean>>>({});

  // Here to avoid re-running the validation if the errors are the same
  const prevErrors = useRef<Partial<MapTo<OTCFieldsData, string>>>(EMPTY_OBJECT);
  const errors = useMemo(() => {
    if (!formData.productType) {
      prevErrors.current = EMPTY_OBJECT;
      return NO_ERRORS;
    }

    const { sliceStrategy, otcFields } = getSliceStrategyForStateWithOTCFields(formData);
    const nextErrors = sliceStrategy.validateOTCFields(otcFields);
    return JSON.stringify(prevErrors.current) === JSON.stringify(nextErrors) ? prevErrors.current : nextErrors;
  }, [formData]);

  const setFieldTouched = useCallback((field: string, isTouched: boolean = true) => {
    setTouched(prev => ({ ...prev, [field]: isTouched }));
  }, []);

  const isValid = Object.keys(errors).length === 0;

  const setAllFieldsTouched = useCallback(() => {
    const { sliceStrategy } = getSliceStrategyForStateWithOTCFields(formData);
    const defaultValues = sliceStrategy.getDefaultValues();
    const loanTouchKeys =
      formData.productType === 'fixedRateLoan'
        ? FIXED_RATE_LOAN_OTC_TOUCH_KEYS
        : formData.productType === 'floatingRateLoan'
          ? FLOATING_RATE_LOAN_OTC_TOUCH_KEYS
          : [];
    const allFieldKeys = new Set([...Object.keys(formData), ...Object.keys(defaultValues || {}), ...loanTouchKeys]);

    setTouched(prev => ({
      ...prev,
      ...Array.from(allFieldKeys).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
    }));
  }, [formData]);

  return { errors, touched, setFieldTouched, isValid, setAllFieldsTouched };
};
